// How one capacity cell reads.
//
// Split out of the grid because "a cell never renders a bare dash" has real
// edge cases -- zero capacity, zero hours, all-unknown, partially-unknown --
// and testing them through a mounted table tests the table, not the rule.
// Same reasoning as filterPrefs.js and statusAge.js.

const EMPTY = { primary: "", secondary: "", unknownNote: "" };

// parseFloat already drops a trailing ".00" ("8.00" -> 8) while keeping a
// real decimal ("8.25" -> 8.25), so String() is the whole formatter; toFixed
// would have to be told how many places to keep and would get it wrong for
// one of the two cases.
export const formatHours = (hours) => {
  const n = parseFloat(hours);
  if (!Number.isFinite(n)) return "";
  return String(n);
};

export function formatCell(cell) {
  if (!cell || !cell.count) return EMPTY;

  const primary = `${cell.count} job${cell.count === 1 ? "" : "s"}`;
  const allUnknown = cell.unknown === cell.count;

  // The old grid printed "—" here, and because most tasks have no estimate,
  // most cells were dashes and the whole page read as empty. Saying what is
  // actually true costs three more characters.
  const secondary = allUnknown ? "no estimate" : `${formatHours(cell.hours)}h`;

  const partial = cell.unknown > 0 && !allUnknown;
  const unknownNote = partial
    ? `${cell.unknown} of ${cell.count} have no estimate`
    : "";

  return { primary, secondary, unknownNote };
}

// How full one person's month is, 0-100, or null when there is no target to
// fill against. Clamped at 100 deliberately: a bar drawn past its own track
// reads as a rendering bug, so the overflow is stated in words instead.
export function capacityFill(hours, capacity) {
  if (capacity == null) return null;
  const h = parseFloat(hours);
  const c = Number(capacity);
  if (!Number.isFinite(h) || h <= 0) return 0;
  if (c <= 0) return 100;
  return Math.min(100, Math.round((h / c) * 100));
}

// `capacity == null` rather than a truthiness check: 0 is a real, meaningful
// target -- "no hours available this month" -- and work booked against it is
// genuinely over capacity. Truthiness would report 'none' and hide that.
export function capacityState(hours, capacity) {
  if (capacity == null) return "none";
  const h = parseFloat(hours);
  const c = Number(capacity);
  if (!Number.isFinite(h)) return "none";
  if (h > c) return "over";
  if (c === 0) return "near";
  return h >= c * 0.85 ? "near" : "under";
}

// Matches UNSCHEDULED in backend/clients/planning.py. Lives here rather than
// in a view component because both the grid and the drawer need it, and a
// constant that moves when a component is renamed is a constant in the wrong
// place.
export const UNSCHEDULED = "unscheduled";
export const OVERDUE = "overdue";

// "2026-09-01" -> "Sep 2026". Three planning surfaces label the same months,
// and three copies of this would be three chances for them to disagree about
// what a month is called.
export const MONTH_LABEL = (iso) => {
  const [y, m] = iso.split("-");
  const name = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ][parseInt(m, 10) - 1];
  return `${name} ${y}`;
};
